import * as THREE from 'three';
import gsap from 'gsap';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import Experience from '../Experience';
import Space from './Space.js';
import Animations from '../Systems/Animations.js';
import threeTheme from '../Utils/theme.js';
// import Time from '../Utils/Time.js';
// import { color } from 'three/src/nodes/TSL.js';

/**
* Sets up properties for already loaded 3d-card model and puts it in a wrapper for easier transformations
*/
export default class Card {

    static ROTATION_STRENGTH = 1.2;
    // static ROTATION_STRENGTH = 3.5;
    static ROTATION_MAX = 0.4;
    static MAX_ROTATION = Math.PI / 2.3;
    static MODEL_NAME = 'card';
    static WRAPPER_NAME = 'wrapper';
    static WRAPPER_DEPTH = 0.025;
    static BACK_SIDE = 0;
    static FRONT_SIDE = 1;
    static DEFAULT_EMISSIVE_COLOR = 0xc98400;
    static UP_QUATERNION = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
    static DOWN_QUATERNION = new THREE.Quaternion();
    static HIGHEST_RENDERORDER = 999;
    static DEFAULT_TEXTURE_MIN_FILTER = 'LinearFilter';
    static DEFAULT_TEXTURE_MAG_FILTER = 'LinearFilter';
    static TEXTURE_MIN_FILTER_MAP = {
        NearestFilter: THREE.NearestFilter,
        LinearFilter: THREE.LinearFilter,
        NearestMipmapNearestFilter: THREE.NearestMipmapNearestFilter,
        NearestMipmapLinearFilter: THREE.NearestMipmapLinearFilter,
        LinearMipmapNearestFilter: THREE.LinearMipmapNearestFilter,
        LinearMipmapLinearFilter: THREE.LinearMipmapLinearFilter,
    };
    static TEXTURE_MAG_FILTER_MAP = {
        NearestFilter: THREE.NearestFilter,
        LinearFilter: THREE.LinearFilter,
    };

    static sharedUniforms = {
        grayValueSplitPoint: new THREE.Uniform(0.0),
    };
    static debugFolder = null;

    static newFromSourceTexture(id, texture, x, y, z, emissiveColor) {
        [x, y, z] = [x || 0, y || 0, z || 0];
        emissiveColor = emissiveColor || Card.DEFAULT_EMISSIVE_COLOR;
        const position = new THREE.Vector3(x, y, z);
        const aspect = texture.image.width / texture.image.height;
        const width = 1;
        const height = width / aspect;
        const geometry = new THREE.PlaneGeometry(width, height);
        geometry.clearGroups();
        geometry.addGroup(0, Infinity, 0);
        geometry.addGroup(0, Infinity, 1);

        const materials = [0, 1].map(_ => new THREE.MeshStandardMaterial({
            transparent: true,
            // alphaTest is important, because it also affects the form of the shadow
            alphaTest: 0.5,
            // polygonOffset is used to achieve z-ordering
            polygonOffset: true,
            polygonOffsetUnits: 1,
            polygonOffsetFactor: 0,
            // highlight using emissive
            emissive: new THREE.Color(emissiveColor),
            emissiveIntensity: 0,
        }));

        const model = new THREE.Mesh(
            mergeGeometries([geometry, geometry.clone().rotateY(Math.PI)], true),
            materials
        )
        model.name = Card.MODEL_NAME;
        const card = new Card(id, model, position, aspect);
        card.setTexture(texture, Card.FRONT_SIDE);
        return card;
    }

    constructor(id, model, initialPosition, aspect) {
        this.id = id;
        this.index = 0;
        this.sortOrder = 0;
        this.experience = new Experience;
        this.time = this.experience.time;
        // this.eventBus = this.experience.eventBus; // remove? not used
        this.scene = this.experience.scene;
        this.space = this.experience.space;
        this.vignetteModifiers = this.experience.vignetteModifiers;


        // Interaction state
        this.targetPosition = (new THREE.Vector3(0, 0, 0)).copy(initialPosition);
        this.lastPosition = (new THREE.Vector3(0, 0, 0)).copy(initialPosition);
        this.aspect = aspect;
        this.width = 1;
        this.height = this.width / aspect;
        this.dragDistance = new THREE.Vector3(0, 0, 0);
        this.rotationStrength = Card.ROTATION_STRENGTH;
        this.rotationMax = Card.ROTATION_MAX;
        this.isSelected = false;
        this.isLifted = false;
        this.isHighLifted = false;
        this.isFaceUp = false;
        this.isFocused = false;
        this.focusAnimationEnded = false;
        this.defocusAnimationEnded = true;
        // this.grayValueSplitPoint = 0.023;

        this.setupModel(model);

        // this.debugMenu();
    }

    get position() { return this.wrapper.position }

    get isFaceDown() { return !this.isFaceUp }

    get isInteractable() { return !this.isFocused }

    select() {
        this.isSelected = true;
        this.liftUp(Space.CARD_LIFTED_Z);
    }

    deselect() {
        this.isSelected = false;
        this.dropDown(Space.CARD_DOWN_Z);
    }

    focus(onComplete) {
        this.isFocused = true;
        this.lastPosition.copy(this.targetPosition);
        this.model.castShadow = false;
        Animations.focusCardToCamera(this, this.vignetteModifiers, this.experience.camera, undefined, onComplete);
    }

    // TODO: There is a bug with remotely defocusing cards.0
    //       When a card is selected in window A, then focused,
    //       then defocused, then the card is defocused and layed down in window B.
    //       But it remains selected in window A.
    defocus(onComplete) {
        this.isFocused = false;
        this.targetPosition.copy(this.lastPosition);
        // Always restore a deterministic z after focus:
        // selected cards stay lifted, non-selected cards drop to table.
        this.targetPosition.z = this.isSelected ? Space.CARD_LIFTED_Z : Space.CARD_DOWN_Z;
        Animations.resetFocusTilt(this);
        this.model.castShadow = true;
        Animations.defocusCard(this, this.vignetteModifiers, this.targetPosition, onComplete);
    }

    tiltTowards(ndcX, ndcY) {
        if (!this.isFocused) return;
        const tiltX = -ndcY;
        const tiltY = ndcX;
        Animations.focusTilt(this, tiltX, tiltY);
    }

    highlightOn() {
        if (this.isHighlighted) return;

        let emissiveIntensity = 0.1;
        if (this.isFaceDown) emissiveIntensity = 0.5;
        Animations.fadeHighlight(this, emissiveIntensity, 0.25);
        this.isHighlighted = true;
    }

    setEmissiveColor(color) {
        this.withMaterials((material) => {
            material.emissive.set(color);
            material.needsUpdate = true;
        });
    }

    highlightOff() {
        if (!this.isHighlighted) return;
        Animations.fadeHighlight(this, 0.0, 0.4);
        this.isHighlighted = false;
    }

    toggleSelect() {
        if (this.isSelected) {
            this.deselect();
        } else {
            this.select();
        }
    }

    liftUp(zTarget) {
        this.isLifted = true;
        this.space.liftedCard = this;
        this.lastPosition.copy(this.targetPosition);
        this.targetPosition.z = zTarget;
        Animations.liftCard(this, zTarget);
        this.space.liftedCard = this;
    }

    dropDown(zTarget) {
        this.isLifted = false;
        this.isHighLifted = false;
        this.space.liftedCard = null;
        this.targetPosition.z = zTarget;
        Animations.dropCard(this, zTarget);
        this.space.liftedCard = null;
    }

    flipFace(onComplete) {
        this.isSelected = false;
        if (this.isFaceUp) this.faceDown(onComplete);
        else this.faceUp(onComplete);
        Animations.fadeHighlight(this, 0.0, 0.4);
    }

    getFaceQuaternion() {
        return this.isFaceUp ? Card.UP_QUATERNION : Card.DOWN_QUATERNION;
    }

    // Reveal Card to faceup and lift up high
    faceUp(onComplete) {
        this.isFaceUp = true;
        var durationFlip = 0.8
        Animations.flipCardQuaternion(this, this.isFaceUp, onComplete, durationFlip);

        if (!this.isFocused) {
            Animations.highLiftCard(this, Space.CARD_HIGHLIFTED_Z);
            this.isHighLifted = true;
        }
    }
    // Flip Card to facedown and put on table
    faceDown(onComplete) {
        this.isFaceUp = false;
        var durationFlip = 0.3
        Animations.flipCardQuaternion(this, this.isFaceUp, onComplete, durationFlip);
        if (this.isFocused) return;

        if (this.isHighLifted) {
            Animations.dropFromHighlifted(this, Space.CARD_DOWN_Z);
        } else {
            Animations.liftAndDropCard(this, Space.CARD_DOWN_Z);
        }
    }

    setPosition(targetPosition) {
        this.lastPosition.copy(this.targetPosition);
        this.targetPosition.copy(targetPosition);
        this.wrapper.position.copy(this.targetPosition);
    }

    beginMotion({ lifted = this.isLifted, clearFocus = false } = {}) {
        if (clearFocus && this.isFocused) {
            this.isFocused = false;
            Animations.resetFocusTilt(this);
            this.model.castShadow = true;
        }

        this.isLifted = !!lifted;
        if (this.isLifted) {
            this.space.liftedCard = this;
        } else if (this.space.liftedCard?.id === this.id) {
            this.space.liftedCard = null;
        }

        this.lastPosition.copy(this.targetPosition);
    }

    updateMotionFrame(targetPosition, { rotationX, rotationY, rotationZ } = {}) {
        if (targetPosition) {
            this.wrapper.position.copy(targetPosition);
            this.targetPosition.copy(targetPosition);
        }

        if (rotationX !== undefined) this.model.rotation.x = rotationX;
        if (rotationY !== undefined) this.model.rotation.y = rotationY;
        if (rotationZ !== undefined) this.model.rotation.z = rotationZ;
    }

    endMotion(finalPosition, { lifted = false, resetRotation = true } = {}) {
        this.isLifted = !!lifted;
        if (this.isLifted) {
            this.space.liftedCard = this;
        } else if (this.space.liftedCard?.id === this.id) {
            this.space.liftedCard = null;
        }

        if (resetRotation) {
            this.model.rotation.set(0, 0, 0);
        }

        if (finalPosition) {
            this.setPosition(finalPosition);
            this.lastPosition.copy(finalPosition);
        }
    }

    animateMotionTo(targetPosition, {
        duration = 0.4,
        ease = 'power1.out',
        lifted = false,
        clearFocus = false,
        resetRotation = false,
        overwrite = 'auto',
        onUpdate,
        onComplete,
    } = {}) {
        const finalPosition = targetPosition.clone ? targetPosition.clone() : new THREE.Vector3(targetPosition.x, targetPosition.y, targetPosition.z);

        this.beginMotion({ lifted, clearFocus });

        return gsap.to(this.wrapper.position, {
            duration,
            ease,
            overwrite,
            x: finalPosition.x,
            y: finalPosition.y,
            z: finalPosition.z,
            onUpdate: () => {
                this.targetPosition.copy(this.wrapper.position);
                if (onUpdate) onUpdate(this);
            },
            onComplete: () => {
                this.endMotion(finalPosition, { lifted, resetRotation });
                if (onComplete) onComplete(this);
            },
        });
    }

    dragTo(targetPosition, rotationMax) {
        rotationMax = rotationMax ?? this.rotationMax;
        this.targetPosition.copy(targetPosition);
        // Calculate Tilt
        // Direction and speed of targetPosition determines tilt (rotation) of card
        this.dragDistance.subVectors(this.targetPosition, this.lastPosition);
        // dragDistanceNormalised.subVectors(pos, posLast).normalize();
        // Clamp delta to prevent frame spikes (e.g. during texture loading) from killing tilt
        const deltaSec = Math.max(this.time.delta, 8) / 1000;
        let dragSpeed = new THREE.Vector2();
        dragSpeed.x = this.dragDistance.x * this.rotationStrength / deltaSec;
        dragSpeed.y = this.dragDistance.y * this.rotationStrength / deltaSec;
        // limit rotation to max
        dragSpeed.x = THREE.MathUtils.clamp(dragSpeed.x, -rotationMax, rotationMax);
        dragSpeed.y = THREE.MathUtils.clamp(dragSpeed.y, -rotationMax, rotationMax);

        this.lastPosition.copy(this.targetPosition);

        // TODO: There is an issue with dragging cards remotely, when they have not yet been dragged here.
        //       A freshly loaded second window does not rotate the cards correctly,
        //       when they are dragged from another window.
        Animations.dragCard(this, this.targetPosition, dragSpeed);
    }

    setIndex(index) {
        this.index = index;
        this.model.material.forEach((material) => material.polygonOffsetFactor = -index);
        if (this.isFocused) index = Card.HIGHEST_RENDERORDER; //so vignette effect doesn't cover our card
        this.model.renderOrder = index;
    }

    withMaterials(fun) {
        [Card.BACK_SIDE, Card.FRONT_SIDE].forEach(side => this.withMaterial(side, fun));
    }

    withMaterial(side, fun) {
        const material = this.model.material[side];
        fun(material);
    }

    getTextureAspect(texture) {
        const image = texture?.image || texture?.source?.data;
        const width = image?.naturalWidth || image?.videoWidth || image?.width;
        const height = image?.naturalHeight || image?.videoHeight || image?.height;
        if (!width || !height) return null;
        return width / height;
    }

    setAspect(aspect) {
        if (!Number.isFinite(aspect) || aspect <= 0) return;
        if (!this.model || !this.wrapper) return;
        if (this.aspect && Math.abs(this.aspect - aspect) < 0.0001) return;

        const previousHeight = this.height || (this.width / this.aspect);
        const nextHeight = this.width / aspect;
        if (!previousHeight || !Number.isFinite(previousHeight)) return;

        const scaleY = nextHeight / previousHeight;
        this.model.scale.y *= scaleY;
        this.wrapper.scale.y *= scaleY;

        this.aspect = aspect;
        this.height = nextHeight;
    }

    setTexture(texture, side) {
        this.configureTextureSampling(texture);
        texture.colorSpace = THREE.SRGBColorSpace;

        this.withMaterial(side, material => {
            material.map = texture;
            material.side = THREE.FrontSide;
            material.transparent = true;
            material.needsUpdate = true;
        });

        if (side === Card.FRONT_SIDE) {
            const aspect = this.getTextureAspect(texture);
            this.setAspect(aspect);
        }
    }

    replaceTexture(url, side) {
        side = side ?? Card.FRONT_SIDE;
        const loader = new THREE.TextureLoader();
        loader.load(url, (texture) => {
            this.setTexture(texture, side);
        });
    }

    getMaxAnisotropy() {
        const renderer = this.experience?.renderer?.instance;
        const caps = renderer?.capabilities;
        if (!caps?.getMaxAnisotropy) return 1;
        return Math.max(caps.getMaxAnisotropy(), 1);
    }

    getTextureMinFilter() {
        const name = this.experience?.settings?.texture_min_filter || Card.DEFAULT_TEXTURE_MIN_FILTER;
        return Card.TEXTURE_MIN_FILTER_MAP[name] || Card.TEXTURE_MIN_FILTER_MAP[Card.DEFAULT_TEXTURE_MIN_FILTER];
    }

    getTextureMagFilter() {
        const name = this.experience?.settings?.texture_mag_filter || Card.DEFAULT_TEXTURE_MAG_FILTER;
        return Card.TEXTURE_MAG_FILTER_MAP[name] || Card.TEXTURE_MAG_FILTER_MAP[Card.DEFAULT_TEXTURE_MAG_FILTER];
    }

    isMipmapFilter(filter) {
        return [
            THREE.NearestMipmapNearestFilter,
            THREE.NearestMipmapLinearFilter,
            THREE.LinearMipmapNearestFilter,
            THREE.LinearMipmapLinearFilter,
        ].includes(filter);
    }

    configureTextureSampling(texture) {
        if (!texture) return;

        const minFilter = this.getTextureMinFilter();
        const magFilter = this.getTextureMagFilter();
        const useMipmaps = this.isMipmapFilter(minFilter);

        texture.generateMipmaps = useMipmaps;
        texture.minFilter = minFilter;
        texture.magFilter = magFilter;
        texture.anisotropy = useMipmaps ? Math.min(8, this.getMaxAnisotropy()) : 1;
        texture.needsUpdate = true;
    }

    setNonColorTextureColorSpace(texture) {
        if (!texture) return;
        if (THREE.NoColorSpace !== undefined) {
            texture.colorSpace = THREE.NoColorSpace;
        } else if (THREE.LinearSRGBColorSpace !== undefined) {
            texture.colorSpace = THREE.LinearSRGBColorSpace;
        }
    }

    refreshTextureSampling() {
        this.withMaterials(material => {
            if (material.map) {
                this.configureTextureSampling(material.map);
                material.map.colorSpace = THREE.SRGBColorSpace;
            }
            if (material.normalMap) {
                this.configureTextureSampling(material.normalMap);
                this.setNonColorTextureColorSpace(material.normalMap);
            }
            if (material.roughnessMap) {
                this.configureTextureSampling(material.roughnessMap);
                this.setNonColorTextureColorSpace(material.roughnessMap);
            }
            material.needsUpdate = true;
        });
    }

    setNonColorTexture(texture) {
        if (!texture) return;
        this.configureTextureSampling(texture);
        this.setNonColorTextureColorSpace(texture);
        texture.needsUpdate = true;
    }

    setNormalMap(texture, side) {
        this.setNonColorTexture(texture);
        this.withMaterial(side, material => {
            material.normalMap = texture;
            material.needsUpdate = true;
        });
    }

    setMetalnessMap(texture, side) { //TODO: rename to setRoughnesMap?
        console.log(`set metalnes: ${this.id}[${side}] -> ${texture}`);
        this.setNonColorTexture(texture);
        this.withMaterial(side, material => {
            // material.metalnessMap = texture; //needs seperate texture
            material.roughnessMap = texture;
            material.roughness = 1.0;
            material.metalness = 0.0;
            material.needsUpdate = true;
        });
    }

    applyCustomShader(vertexShader, fragmentShader, side) {

        const u_grayValueSplitPoint = Card.sharedUniforms.grayValueSplitPoint;

        this.withMaterial(side, material => {
            const u_tint_bright = new THREE.Uniform(threeTheme['card-bright']);
            const u_tint_brightest = new THREE.Uniform(threeTheme['card-brightest']);
            const u_tint_dark = new THREE.Uniform(threeTheme['card-dark']);
            const u_tint_darkest = new THREE.Uniform(threeTheme['card-darkest']);
            // const u_grayValueSplitPoint = new THREE.Uniform(grayValueSplitPoint);
            material.userData.u_tint_bright = u_tint_bright;
            material.userData.u_tint_brightest = u_tint_brightest;
            material.userData.u_tint_dark = u_tint_dark;
            material.userData.u_tint_darkest = u_tint_darkest;
            material.userData.u_grayValueSplitPoint = u_grayValueSplitPoint;

            material.onBeforeCompile = shader => {
                shader.uniforms.u_tint_bright = u_tint_bright;
                shader.uniforms.u_tint_brightest = u_tint_brightest;
                shader.uniforms.u_tint_dark = u_tint_dark;
                shader.uniforms.u_tint_darkest = u_tint_darkest;
                shader.uniforms.u_grayValueSplitPoint = u_grayValueSplitPoint;

                shader.fragmentShader = shader.fragmentShader
                    .replace('void main() {',
                        'uniform vec3 u_tint_bright;uniform vec3 u_tint_dark;uniform vec3 u_tint_brightest;uniform vec3 u_tint_darkest;uniform float u_grayValueSplitPoint;\nvoid main() {')
                    .replace('#include <map_fragment>', fragmentShader);
            };

            material.needsUpdate = true;
        });
    }


    /**
    * Puts the model in a simple wrapper for easier transformation.
    * @summary When moving a 3d Object from its origin the rotation is not applied locally anymore. This wrapper prevents that when we move the wrapper but rotate the model
    */
    setupModel(model) {
        this.model = model;
        this.model.castShadow = true;
        this.model.receiveShadow = true;

        // TODO: replace with multiple of scale, in case we scale the model
        const geometry = new THREE.BoxGeometry(this.width, this.height, Card.WRAPPER_DEPTH);
        const material = new THREE.MeshStandardMaterial({
            color: 0x00ff00,
            wireframe: true,
            visible: false
        });
        this.wrapper = new THREE.Mesh(geometry, material);

        this.wrapper.name = Card.WRAPPER_NAME;
        this.wrapper.add(this.model);
        this.wrapper.userData.id = this.id;
        this.wrapper.userData.instance = this;
        this.wrapper.position.copy(this.targetPosition);

        this.scene.add(this.wrapper);
    }

    /**
    * Shows wrapper mesh when #debug is on
    */
    toggleWrapperVisuals() {
        if (this.wrapper.material.visible == false) {
            this.wrapper.material.visible = true;
        } else {
            this.wrapper.material.visible = false;
        }
    }

    update(delta) {
        // Nothing as of now
    }

    debugMenu() {
        // add debug button to turn on/off the wrapper visuals
        // if (this.experience.debug) {
        //     this.debugFolder = this.experience.debug.addFolder(this.id);
        //     const debugObject = {
        //         toggleWrapperWireframe: () => { this.toggleWrapperVisuals() }
        //     }
        //     this.debugFolder.add(debugObject, 'toggleWrapperWireframe');
        //     this.debugFolder.addObject3D('wrapper', this.wrapper);
        //     this.debugFolder.close();
        // }
    }

}
